/**
 * §21.3 GastroGest — Ticket promedio + volumen de órdenes por hora del día y día de
 * la semana (patrones de staffing/horarios). NO expone costo ni margen, así que es
 * visible a roles operativos (pos:view) además del dueño. Reusa DatePeriodFilter y
 * el cliente api(); se hidrata de GET /pos/insights/ticket-volume (mismo SQL de
 * hora pico / mejores días que el overview de Insights — server-side, no duplicado).
 */
import { useEffect, useMemo, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Clock, CalendarDays } from 'lucide-react';
import { api } from '../lib/api';
import { DatePeriodFilter, periodToQuery, periodLabel, defaultPeriod, type PeriodValue } from '../components/DatePeriodFilter';

const mxn = (v: any) =>
  `$${Number(v ?? 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const int = (v: any) => Number(v ?? 0).toLocaleString('es-MX');

interface Bucket { orders: number; total: number; avgTicket: number; }
interface HourRow extends Bucket { hour: number; }
interface WeekdayRow extends Bucket { dow: number; label: string; }
interface TicketData {
  from: string; to: string;
  totals: Bucket;
  byHour: HourRow[];
  byWeekday: WeekdayRow[];
  // PR3 — reservas en línea como FILA PROPIA (no se mezclan en el ticket promedio).
  onlineReservations?: { count: number; sales: number; allBranches: boolean };
}

type Metric = 'orders' | 'avgTicket' | 'total';
const METRICS: Array<{ v: Metric; l: string }> = [
  { v: 'orders', l: '# órdenes' },
  { v: 'avgTicket', l: 'Ticket prom.' },
  { v: 'total', l: 'Total' },
];
const fmtMetric = (m: Metric, v: any) => (m === 'orders' ? int(v) : mxn(v));

function Tile({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white px-5 py-3">
      <p className="text-[11px] uppercase tracking-wide text-slate-400">{label}</p>
      <p className="font-serif text-2xl text-slate-900 tabular-nums">{value}</p>
      {hint && <p className="text-[11px] text-slate-400">{hint}</p>}
    </div>
  );
}

function Card({ title, icon, children, action }: { title: string; icon?: any; children: any; action?: any }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="flex items-center gap-2 font-serif text-lg text-slate-900">{icon}{title}</h3>
        {action}
      </div>
      {children}
    </section>
  );
}

export function TicketVolumeView({ sourceModule = 'gastro' }: { sourceModule?: 'gastro' | 'boutique' | 'gym' }) {
  const [period, setPeriod] = useState<PeriodValue>(defaultPeriod());
  const [data, setData] = useState<TicketData | null>(null);
  const [loading, setLoading] = useState(true);
  const [metric, setMetric] = useState<Metric>('orders');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const qs = new URLSearchParams(periodToQuery(period));
    qs.set('sourceModule', sourceModule);
    api(`/pos/insights/ticket-volume?${qs.toString()}`)
      .then((r) => { if (!cancelled) setData(r as TicketData); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [period, sourceModule]);

  const businessHours = useMemo(() => data?.byHour?.filter((h) => h.hour >= 7 && h.hour <= 23) ?? [], [data]);
  const peakHour = useMemo(() => data?.byHour?.slice().sort((a, b) => b.orders - a.orders)[0], [data]);
  const bestDay = useMemo(() => data?.byWeekday?.slice().sort((a, b) => b.orders - a.orders)[0], [data]);
  const t = data?.totals;

  const MetricToggle = (
    <div className="inline-flex rounded-lg border border-slate-200 p-0.5 text-[11px]">
      {METRICS.map((m) => (
        <button key={m.v} onClick={() => setMetric(m.v)} className={`rounded px-2 py-0.5 ${metric === m.v ? 'bg-slate-900 text-white' : 'text-slate-500'}`}>{m.l}</button>
      ))}
    </div>
  );

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-serif text-2xl tracking-tight text-slate-900">Ticket y volumen</h2>
          <p className="text-sm text-slate-500">Cuándo y cuánto se vende — para planear horarios y personal · {periodLabel(period)}.</p>
        </div>
        <DatePeriodFilter value={period} onChange={setPeriod} />
      </div>

      {loading ? <p className="text-sm text-slate-500">Cargando…</p> : !data ? (
        <p className="text-sm text-slate-400">Sin datos para el período.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Tile label="Ticket promedio" value={mxn(t?.avgTicket)} />
            <Tile label="# órdenes" value={int(t?.orders)} />
            <Tile label="Total vendido" value={mxn(t?.total)} />
            <Tile label="Hora pico" value={peakHour && peakHour.orders ? `${peakHour.hour}:00` : '—'} hint={peakHour && peakHour.orders ? `${int(peakHour.orders)} órdenes` : ''} />
          </div>

          {/* PR3 — reservas en línea como FILA PROPIA (no entran al ticket promedio). */}
          {data.onlineReservations && (data.onlineReservations.count > 0 || data.onlineReservations.sales > 0) && (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-emerald-200 bg-emerald-50/40 px-5 py-3 text-sm">
              <span className="font-medium text-slate-700">
                Reservas en línea{data.onlineReservations.allBranches ? ' (todas las sucursales)' : ''}
              </span>
              <span className="tabular-nums text-slate-800">
                <b>{int(data.onlineReservations.count)}</b> reserva(s) · <b>{mxn(data.onlineReservations.sales)}</b> cobrado
              </span>
            </div>
          )}

          <Card title="Por hora del día" icon={<Clock className="h-4 w-4 text-slate-500" />} action={MetricToggle}>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={businessHours} margin={{ top: 6, right: 8, left: -16, bottom: 0 }}>
                <XAxis dataKey="hour" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} interval={1} tickFormatter={(h: number) => `${h}`} />
                <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} width={48}
                  tickFormatter={(v: number) => (metric !== 'orders' && v >= 1000 ? `${Math.round(v / 1000)}k` : `${v}`)} />
                <Tooltip formatter={(v: any) => fmtMetric(metric, v)} labelFormatter={(h) => `${h}:00 h`} />
                <Bar dataKey={metric} radius={[3, 3, 0, 0]}>
                  {businessHours.map((h, i) => <Cell key={i} fill={peakHour && h.hour === peakHour.hour && metric === 'orders' ? '#b45309' : '#0f766e'} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>

          <Card title="Por día de la semana" icon={<CalendarDays className="h-4 w-4 text-slate-500" />} action={MetricToggle}>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.byWeekday} margin={{ top: 6, right: 8, left: -16, bottom: 0 }}>
                <XAxis dataKey="label" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} width={48}
                  tickFormatter={(v: number) => (metric !== 'orders' && v >= 1000 ? `${Math.round(v / 1000)}k` : `${v}`)} />
                <Tooltip formatter={(v: any) => fmtMetric(metric, v)} labelFormatter={(l) => `Día: ${l}`} />
                <Bar dataKey={metric} radius={[4, 4, 0, 0]}>
                  {data.byWeekday.map((d, i) => <Cell key={i} fill={bestDay && d.dow === bestDay.dow && metric === 'orders' ? '#0f766e' : '#94a3b8'} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </>
      )}
    </div>
  );
}
